import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import * as XLSX from 'xlsx';
import { AuthService } from 'src/app/auth.service';
import { LambdaService } from 'src/app/lambda.service';
import { ThemingService } from 'src/app/theming.service';
import {
  BillingReportHeader,
  BillingReportHistoryItem,
  BillingReportHistoryResponse,
  BillingReportResponse,
  BillingReportRow,
  BillingReportSummary,
  GenerateBillingReportPayload,
  ReportCatalogOption,
  ReportType,
  SalesReportExcelRow,
  SalesReportFilters,
  SalesReportItem,
  SalesReportPreviewRow,
  SalesReportResponse,
  SalesReportSale,
  SalesReportSummary,
  ValidatedSalesReportExcelRow,
} from './reports.models';
import {
  extractShopCompanies,
  ShopCompany,
} from 'src/app/components/shop-companies/shop-company.models';

type ExcelCell = XLSX.CellObject & { z?: string };

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit {
  readonly pageSizeOptions = [10, 20, 50];
  readonly reportTypeOptions: Array<{ id: ReportType; labelKey: string }> = [
    { id: 'sales', labelKey: 'SALES_EXPORT_REPORT' },
    { id: 'activePlans', labelKey: 'BILLING_REPORT' },
  ];
  readonly saleStateOptions: ReportCatalogOption[] = [
    { id: -1, name: 'Todos' },
    { id: 0, name: 'Cancelado' },
    { id: 1, name: 'Pendiente de verificación' },
    { id: 2, name: 'Preparando orden' },
    { id: 3, name: 'Entregado' },
    { id: 4, name: 'Pendiente Compra Click' },
    { id: 5, name: 'Completado' },
    { id: 6, name: 'Parcial' },
  ];
  readonly excelHeaders = [
    'Identificación',
    'Nombre',
    'Documento',
    'Fecha',
    'Monto Principal',
    'Monto Descuento',
    'Monto Neto',
    'Plazo Meses',
    'Fecha Primer Cuota',
    'Cuota Mensual',
    'Observaciones',
  ];

  filtersForm: FormGroup;
  isLoading = false;
  isGenerating = false;
  isExporting = false;
  isHistoryLoading = false;
  hasSearched = false;
  showAdditionalFilters = false;
  errorMessage = '';
  billingMessage = '';
  historyErrorMessage = '';
  reportResponse: SalesReportResponse | null = null;
  summary: SalesReportSummary = {};
  billingReportResponse: BillingReportResponse | null = null;
  billingSummary: BillingReportSummary = {};
  billingRows: BillingReportRow[] = [];
  billingHistory: BillingReportHistoryItem[] = [];
  generatedBillingReportId: number | string | null = null;
  previewRows: SalesReportPreviewRow[] = [];
  validExcelRows: ValidatedSalesReportExcelRow[] = [];
  invalidExcelRows: SalesReportExcelRow[] = [];
  paymentTypes: ReportCatalogOption[] = [];
  shopPoints: ReportCatalogOption[] = [];
  shopCompanies: ReportCatalogOption[] = [];
  currentPage = 1;
  pageSize = 10;

  constructor(
    private fb: FormBuilder,
    private lambdaService: LambdaService,
    private authService: AuthService,
    public themingService: ThemingService
  ) {
    this.filtersForm = this.fb.group(
      {
        reportType: ['sales' as ReportType],
        startDate: [''],
        endDate: [''],
        minAmount: ['', [this.nonNegativeNumberValidator()]],
        maxAmount: ['', [this.nonNegativeNumberValidator()]],
        searchString: [''],
        productId: [''],
        saleState: [-1],
        paymentTypeId: [''],
        shopPointId: [''],
        shopCompanyId: [''],
        paymentTypeMode: [''],
        productPaymentMode: [''],
      },
      {
        validators: [
          this.dateRangeValidator(),
          this.amountRangeValidator(),
          this.billingDatesRequiredValidator(),
        ],
      }
    );
  }

  ngOnInit(): void {
    this.loadCatalogs();
  }

  get totalPages(): number {
    const totalRows = this.isActivePlansReport ? this.billingRows.length : this.previewRows.length;
    return Math.max(1, Math.ceil(totalRows / this.pageSize));
  }

  get pagedSales(): SalesReportPreviewRow[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.previewRows.slice(start, start + this.pageSize);
  }

  get pagedBillingRows(): BillingReportRow[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.billingRows.slice(start, start + this.pageSize);
  }

  get selectedReportType(): ReportType {
    return this.filtersForm.get('reportType')?.value === 'activePlans' ? 'activePlans' : 'sales';
  }

  get isSalesReport(): boolean {
    return this.selectedReportType === 'sales';
  }

  get isActivePlansReport(): boolean {
    return this.selectedReportType === 'activePlans';
  }

  get reportTitleKey(): string {
    return this.isActivePlansReport ? 'BILLING_REPORT' : 'SALES_EXPORT_REPORT';
  }

  get canDownloadExcel(): boolean {
    return this.isSalesReport && !this.isLoading && !this.isExporting && this.validExcelRows.length > 0;
  }

  get canGenerateBillingReport(): boolean {
    const status = this.billingReportResponse?.report?.status;
    return this.isActivePlansReport
      && this.hasSearched
      && this.billingRows.length > 0
      && (status === null || status === undefined || status === 'PREVIEW')
      && !this.isLoading
      && !this.isGenerating
      && !this.isExporting
      && this.generatedBillingReportId === null;
  }

  async loadCatalogs(): Promise<void> {
    await Promise.all([
      this.loadPaymentTypes(),
      this.loadShopPoints(),
      this.loadShopCompanies(),
    ]);
  }

  async loadReport(): Promise<void> {
    if (this.isActivePlansReport) {
      await this.loadBillingPreview();
      return;
    }

    await this.loadSalesReport();
  }

  async loadSalesReport(): Promise<void> {
    this.errorMessage = '';
    this.billingMessage = '';
    this.filtersForm.markAllAsTouched();

    if (!this.themingService.shopId) {
      this.errorMessage = 'REPORT_SHOP_REQUIRED';
      return;
    }

    if (this.filtersForm.invalid || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.hasSearched = true;
    this.clearSalesReportData();

    try {
      const filters = this.buildFilters();
      const response = await this.lambdaService.getSalesReport(filters);
      this.reportResponse = this.normalizeResponse(response);
      this.summary = this.reportResponse.summary;
      this.validateExcelRows(this.reportResponse.excelRows);
      this.previewRows = this.reportResponse.sales.map((sale) => this.toPreviewRow(sale));
      this.currentPage = 1;
    } catch (error) {
      console.error('Error loading report:', error);
      this.clearSalesReportData();
      this.errorMessage = 'REPORT_LOAD_ERROR';
    } finally {
      this.isLoading = false;
    }
  }

  async loadBillingPreview(): Promise<void> {
    this.errorMessage = '';
    this.billingMessage = '';
    this.filtersForm.markAllAsTouched();

    const requestedShopId = this.themingService.shopId;
    if (!requestedShopId) {
      this.errorMessage = 'REPORT_SHOP_REQUIRED';
      return;
    }

    const { startDate, endDate } = this.getBillingDateRange();
    const requestedShopCompanyId = this.getBillingShopCompanyId();
    if (!startDate || !endDate) {
      this.errorMessage = 'REPORT_BILLING_DATES_REQUIRED';
      return;
    }

    if (this.filtersForm.invalid || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.hasSearched = true;
    this.clearBillingPreviewData();

    try {
      const response = this.normalizeBillingResponse(await this.lambdaService.getBillingReports({
        action: 'preview',
        shopId: requestedShopId,
        startDate,
        endDate,
        shopCompanyId: requestedShopCompanyId,
      }));
      const currentRange = this.getBillingDateRange();
      if (
        this.themingService.shopId !== requestedShopId
        || currentRange.startDate !== startDate
        || currentRange.endDate !== endDate
        || this.getBillingShopCompanyId() !== requestedShopCompanyId
      ) {
        return;
      }
      this.billingReportResponse = response;
      this.billingSummary = this.extractBillingSummary(response);
      this.billingRows = response.rows;
      const status = response.report?.status;
      this.generatedBillingReportId = status === 'ALREADY_GENERATED'
        ? response.report?.existingBillingReportId ?? 'ALREADY_GENERATED'
        : null;
      this.billingMessage = response.report?.message
        || response.message
        || (status === 'ALREADY_GENERATED' ? 'REPORT_ALREADY_GENERATED' : status === 'EMPTY' ? 'REPORT_NO_NEW_RECORDS' : '');
      this.currentPage = 1;
    } catch (error) {
      console.error('Error loading billing report preview:', error);
      this.clearBillingPreviewData();
      this.errorMessage = 'REPORT_BILLING_PREVIEW_ERROR';
    } finally {
      this.isLoading = false;
    }
  }

  async generateAndDownloadBillingReport(): Promise<void> {
    if (!this.canGenerateBillingReport) {
      return;
    }

    const { startDate, endDate } = this.getBillingDateRange();
    const shopCompanyId = this.getBillingShopCompanyId();
    if (!this.themingService.shopId || !startDate || !endDate) {
      this.errorMessage = !this.themingService.shopId ? 'REPORT_SHOP_REQUIRED' : 'REPORT_BILLING_DATES_REQUIRED';
      return;
    }

    this.errorMessage = '';
    this.billingMessage = '';
    this.isGenerating = true;

    try {
      const createdBy = await this.getBillingCreatedBy();
      if (!createdBy) {
        this.errorMessage = 'REPORT_GENERATE_ERROR';
        return;
      }

      const payload: GenerateBillingReportPayload = {
        shopId: this.themingService.shopId,
        startDate,
        endDate,
        shopCompanyId,
        createdBy,
      };

      const response = this.normalizeBillingResponse(await this.lambdaService.generateBillingReport(payload));
      const status = response.report?.status;
      const billingReportId = response.billingReportId ?? response.report?.billingReportId ?? null;

      if (status !== 'GENERATED' || billingReportId === null || billingReportId === undefined) {
        this.billingReportResponse = response;
        this.billingSummary = this.extractBillingSummary(response);
        this.billingRows = response.rows;
        this.generatedBillingReportId = status === 'ALREADY_GENERATED'
          ? response.report?.existingBillingReportId ?? 'ALREADY_GENERATED'
          : null;
        this.billingMessage = response.report?.message
          || response.message
          || (status === 'ALREADY_GENERATED' ? 'REPORT_ALREADY_GENERATED' : 'REPORT_NO_NEW_RECORDS');
        return;
      }

      if (!response.excelRows.length) {
        this.errorMessage = 'REPORT_EXPORT_ERROR';
        return;
      }

      this.generatedBillingReportId = billingReportId;
      this.applyGeneratedReport(response);
      this.generatedBillingReportId = billingReportId;
      this.billingMessage = response.report?.message || response.message || 'REPORT_GENERATED_SUCCESS';
      this.isExporting = true;
      try {
        const workbook = this.buildWorkbook(response.excelRows);
        this.writeWorkbook(workbook, this.buildBillingFileName(response.report?.startDate || startDate, response.report?.endDate || endDate));
      } catch (error) {
        console.error('Error generating billing report Excel:', error);
        this.errorMessage = 'REPORT_EXPORT_ERROR';
      } finally {
        this.isExporting = false;
      }

      await this.loadBillingHistory();
    } catch (error) {
      console.error('Error generating billing report:', error);
      this.errorMessage = 'REPORT_GENERATE_ERROR';
    } finally {
      this.isGenerating = false;
      this.isExporting = false;
    }
  }

  async loadBillingHistory(): Promise<void> {
    if (!this.themingService.shopId || this.isHistoryLoading) {
      return;
    }

    this.isHistoryLoading = true;
    this.historyErrorMessage = '';

    try {
      const response = this.normalizeBillingHistoryResponse(await this.lambdaService.getBillingReports({
        action: 'history',
        shopId: this.themingService.shopId,
        amount: 20,
      }));
      this.billingHistory = response.reports;
    } catch (error) {
      console.error('Error loading billing report history:', error);
      this.historyErrorMessage = 'REPORT_HISTORY_LOAD_ERROR';
    } finally {
      this.isHistoryLoading = false;
    }
  }

  async downloadBillingHistoryReport(report: BillingReportHistoryItem): Promise<void> {
    if (!this.themingService.shopId || !report.billingReportId || this.isExporting) {
      return;
    }

    this.errorMessage = '';
    this.isExporting = true;

    try {
      const response = this.normalizeBillingResponse(await this.lambdaService.getBillingReports({
        action: 'getReport',
        shopId: this.themingService.shopId,
        billingReportId: report.billingReportId,
      }));

      if (!response.excelRows.length) {
        this.errorMessage = 'REPORT_EXPORT_ERROR';
        return;
      }

      const workbook = this.buildWorkbook(response.excelRows);
      this.writeWorkbook(
        workbook,
        this.buildBillingFileName(
          response.report?.startDate || report.startDate,
          response.report?.endDate || report.endDate
        )
      );
    } catch (error) {
      console.error('Error downloading billing history report:', error);
      this.errorMessage = 'REPORT_EXPORT_ERROR';
    } finally {
      this.isExporting = false;
    }
  }

  clearFilters(): void {
    const reportType = this.selectedReportType;
    this.filtersForm.reset({
      reportType,
      startDate: '',
      endDate: '',
      minAmount: '',
      maxAmount: '',
      searchString: '',
      productId: '',
      saleState: -1,
      paymentTypeId: '',
      shopPointId: '',
      shopCompanyId: reportType === 'activePlans' ? 0 : '',
      paymentTypeMode: '',
      productPaymentMode: '',
    });
    this.errorMessage = '';
    this.billingMessage = '';
    this.hasSearched = false;
    this.currentPage = 1;
    this.clearReportData();
  }

  onReportTypeChange(value: string): void {
    const reportType: ReportType = value === 'activePlans' ? 'activePlans' : 'sales';
    this.filtersForm.patchValue({
      reportType,
      startDate: '',
      endDate: '',
      minAmount: '',
      maxAmount: '',
      searchString: '',
      productId: '',
      saleState: -1,
      paymentTypeId: '',
      shopPointId: '',
      shopCompanyId: reportType === 'activePlans' ? 0 : '',
      paymentTypeMode: '',
      productPaymentMode: '',
    }, { emitEvent: false });
    this.errorMessage = '';
    this.billingMessage = '';
    this.historyErrorMessage = '';
    this.hasSearched = false;
    this.currentPage = 1;
    this.showAdditionalFilters = false;
    this.clearReportData();
    this.billingHistory = [];

    if (reportType === 'activePlans') {
      void this.loadBillingHistory();
    }
  }

  onBillingDateRangeChange(): void {
    if (!this.isActivePlansReport) {
      return;
    }
    this.errorMessage = '';
    this.billingMessage = '';
    this.hasSearched = false;
    this.currentPage = 1;
    this.clearBillingPreviewData();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.currentPage = page;
  }

  onPageSizeChange(value: string): void {
    const nextSize = Number(value);
    this.pageSize = this.pageSizeOptions.includes(nextSize) ? nextSize : 10;
    this.currentPage = 1;
  }

  async downloadExcel(): Promise<void> {
    if (!this.canDownloadExcel) {
      return;
    }

    this.isExporting = true;
    try {
      const workbook = this.buildWorkbook(this.validExcelRows);
      this.writeWorkbook(workbook, this.buildFileName());
    } catch (error) {
      console.error('Error generating sales report Excel:', error);
      this.errorMessage = 'REPORT_EXPORT_ERROR';
    } finally {
      this.isExporting = false;
    }
  }

  buildFilters(): SalesReportFilters {
    const raw = this.filtersForm.getRawValue();
    const reportType = raw.reportType === 'activePlans' ? 'activePlans' : 'sales';
    const filters: SalesReportFilters = {
      shopId: this.themingService.shopId,
      reportType,
    };

    if (reportType === 'activePlans') {
      return filters;
    }

    this.appendFilter(filters, 'startDate', raw.startDate);
    this.appendFilter(filters, 'endDate', raw.endDate);
    this.appendFilter(filters, 'minAmount', raw.minAmount);
    this.appendFilter(filters, 'maxAmount', raw.maxAmount);
    this.appendFilter(filters, 'searchString', raw.searchString);
    this.appendFilter(filters, 'shopPointId', raw.shopPointId);
    this.appendFilter(filters, 'shopCompanyId', raw.shopCompanyId);
    this.appendFilter(filters, 'paymentTypeId', raw.paymentTypeId);
    this.appendFilter(filters, 'paymentTypeMode', raw.paymentTypeMode);
    this.appendFilter(filters, 'productPaymentMode', raw.productPaymentMode);

    if (raw.saleState !== null && raw.saleState !== undefined && String(raw.saleState) !== '-1') {
      filters.saleState = raw.saleState;
    }

    return filters;
  }

  normalizeResponse(response: unknown): SalesReportResponse {
    const source = this.parseBody(response);
    const record = this.toRecord(source);
    return {
      reportType: 'sales',
      summary: this.normalizeSummary(this.toRecord(record['summary'])),
      sales: Array.isArray(record['sales']) ? record['sales'] as SalesReportSale[] : [],
      items: Array.isArray(record['items']) ? record['items'] as SalesReportItem[] : [],
      excelRows: Array.isArray(record['excelRows']) ? record['excelRows'] as SalesReportExcelRow[] : [],
      filters: {
        shopId: this.themingService.shopId,
        ...this.toRecord(record['filters']),
        reportType: 'sales',
      } as SalesReportFilters,
    };
  }

  validateExcelRows(rows: SalesReportExcelRow[]): void {
    const validRows: ValidatedSalesReportExcelRow[] = [];
    const invalidRows: SalesReportExcelRow[] = [];

    rows.forEach((row) => {
      const normalized = this.normalizeExcelRow(row);
      if (this.isExportableRow(normalized)) {
        validRows.push(normalized);
      } else {
        invalidRows.push(row);
      }
    });

    this.validExcelRows = validRows;
    this.invalidExcelRows = invalidRows;
  }

  buildWorkbook(rows: SalesReportExcelRow[]): XLSX.WorkBook {
    const worksheetRows = rows.map((row) => [
      String(row.identification ?? '').trim(),
      this.normalizeWhitespace(row.name),
      row.document ?? 0,
      this.parseLocalDate(row.date),
      this.toNumber(row.principalAmount),
      this.toNumber(row.discountAmount),
      this.toNumber(row.netAmount),
      this.toNumber(row.termMonths),
      this.parseLocalDate(row.firstInstallmentDate),
      this.toNumber(row.monthlyInstallment),
      this.normalizeWhitespace(row.observations),
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([this.excelHeaders, ...worksheetRows], {
      cellDates: true,
    });

    worksheet['!cols'] = [
      { wch: 13 },
      { wch: 33 },
      { wch: 26 },
      { wch: 12 },
      { wch: 13 },
      { wch: 13 },
      { wch: 12 },
      { wch: 10 },
      { wch: 15 },
      { wch: 13 },
      { wch: 22 },
    ];

    for (let index = 0; index < rows.length; index++) {
      const excelRow = index + 2;
      this.setTextCell(worksheet, `A${excelRow}`, String(rows[index].identification ?? '').trim());
      this.setDateFormat(worksheet, `D${excelRow}`);
      this.setDateFormat(worksheet, `I${excelRow}`);
      ['E', 'F', 'G', 'J'].forEach((column) => this.setMoneyFormat(worksheet, `${column}${excelRow}`));
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Machote Padron');
    return workbook;
  }

  writeWorkbook(workbook: XLSX.WorkBook, fileName: string): void {
    XLSX.writeFile(workbook, fileName);
  }

  buildFileName(): string {
    const raw = this.filtersForm.getRawValue();
    if (this.isActivePlansReport) {
      return this.buildBillingFileName(raw.startDate, raw.endDate);
    }

    const start = this.safeFilePart(raw.startDate);
    const end = this.safeFilePart(raw.endDate);
    if (start && end) {
      return `Reporte_Ventas_${start}_${end}.xlsx`;
    }
    return `Reporte_Ventas_${this.getTodayIsoDate()}.xlsx`;
  }

  buildBillingFileName(startDate: unknown, endDate: unknown): string {
    return `reporte-cobros-${this.toIsoDatePart(startDate)}-${this.toIsoDatePart(endDate)}.xlsx`;
  }

  billingTypeLabel(value: unknown): string {
    if (value === 'RECURRING_MONTHLY') {
      return 'BILLING_TYPE_RECURRING_MONTHLY';
    }
    if (value === 'INSTALLMENT') {
      return 'BILLING_TYPE_INSTALLMENT';
    }
    return value === null || value === undefined || String(value).trim() === '' ? '-' : String(value);
  }

  formatMoney(value: unknown): string {
    const amount = this.toNumber(value);
    const theme = this.themingService.shopTheming?.[0];
    try {
      return amount.toLocaleString(theme?.countryLanguage || 'es-CR', {
        style: 'currency',
        currency: theme?.currencyCode || 'CRC',
      });
    } catch {
      return amount.toFixed(2);
    }
  }

  formatDate(value: unknown): string {
    const date = this.toDisplayDate(value);
    return date || '-';
  }

  numberValue(value: unknown): number {
    return this.toNumber(value);
  }

  hasFieldError(fieldName: string, errorName: string): boolean {
    const field = this.filtersForm.get(fieldName);
    return !!field && field.touched && field.hasError(errorName);
  }

  hasFormError(errorName: string): boolean {
    return this.filtersForm.touched && this.filtersForm.hasError(errorName);
  }

  private async loadPaymentTypes(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getPaymentMethods(this.themingService.shopId);
      const paymentTypes = Array.isArray(response?.paymentTypes) ? response.paymentTypes : [];
      this.paymentTypes = paymentTypes
        .filter((paymentType: any) => paymentType?.paymentTypeId !== undefined && paymentType?.paymentTypeName)
        .map((paymentType: any) => ({
          id: paymentType.paymentTypeId,
          name: String(paymentType.paymentTypeName),
        }));
    } catch (error) {
      console.error('Error loading report payment types:', error);
      this.paymentTypes = [];
    }
  }

  private async loadShopPoints(): Promise<void> {
    const themingShopPoints = this.themingService.shopTheming?.[0]?.shopPoints;
    if (Array.isArray(themingShopPoints) && themingShopPoints.length > 0) {
      this.shopPoints = this.mapShopPoints(themingShopPoints);
      return;
    }

    try {
      const response: any = await this.lambdaService.getShopPoints(this.themingService.shopId);
      this.shopPoints = this.mapShopPoints(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error loading report shop points:', error);
      this.shopPoints = [];
    }
  }

  private async loadShopCompanies(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getShopCompanies(this.themingService.shopId, true);
      const companies: ShopCompany[] = extractShopCompanies(response).filter((company) => company.status === 1);
      this.shopCompanies = companies.map((company) => ({
        id: company.shopCompanyId,
        name: company.companyName || company.companyLegalName || String(company.shopCompanyId),
      }));
    } catch (error) {
      console.error('Error loading report shop companies:', error);
      this.shopCompanies = [];
    }
  }

  private mapShopPoints(shopPoints: any[]): ReportCatalogOption[] {
    return shopPoints
      .filter((shopPoint) => shopPoint?.shopPointId !== undefined && shopPoint?.shopPointName)
      .map((shopPoint) => ({
        id: shopPoint.shopPointId,
        name: String(shopPoint.shopPointName),
      }));
  }

  private clearReportData(): void {
    this.clearSalesReportData();
    this.clearBillingPreviewData();
  }

  private clearSalesReportData(): void {
    this.reportResponse = null;
    this.summary = {};
    this.previewRows = [];
    this.validExcelRows = [];
    this.invalidExcelRows = [];
  }

  private clearBillingPreviewData(): void {
    this.billingReportResponse = null;
    this.billingSummary = {};
    this.billingRows = [];
    this.generatedBillingReportId = null;
  }

  private applyGeneratedReport(response: BillingReportResponse): void {
    this.billingReportResponse = response;
    this.billingSummary = this.extractBillingSummary(response);
    this.billingRows = response.rows;
    this.billingMessage = response.report?.message || response.message || '';
    this.hasSearched = true;
    this.currentPage = 1;
  }

  private appendFilter(filters: SalesReportFilters, key: keyof SalesReportFilters, value: unknown): void {
    if (value === null || value === undefined || String(value).trim() === '') {
      return;
    }
    (filters as unknown as Record<string, unknown>)[key] = value;
  }

  private normalizeSummary(summary: Record<string, unknown>): SalesReportSummary {
    return {
      ...summary,
      totalSales: this.toNumber(summary['totalSales']),
      totalClients: this.toNumber(summary['totalClients']),
      totalAssociations: this.toNumber(summary['totalAssociations']),
      totalPrincipalAmount: this.toNumber(summary['totalPrincipalAmount']),
      totalDiscountAmount: this.toNumber(summary['totalDiscountAmount']),
      totalNetAmount: this.toNumber(summary['totalNetAmount']),
      totalMonthlyInstallment: this.toNumber(summary['totalMonthlyInstallment']),
      totalPaidAmount: this.toNumber(summary['totalPaidAmount']),
      calculatedPendingBalance: this.toNumber(summary['calculatedPendingBalance']),
      totalPaymentsCount: this.toNumber(summary['totalPaymentsCount']),
    };
  }

  private normalizeBillingResponse(response: unknown): BillingReportResponse {
    const source = this.parseBody(response);
    const record = this.toRecord(source);
    return {
      action: record['action'] as BillingReportResponse['action'],
      billingReportId: record['billingReportId'] as BillingReportResponse['billingReportId'],
      report: this.toNullableRecord(record['report']) as BillingReportHeader | null,
      summary: this.toRecord(record['summary']) as BillingReportSummary,
      rows: Array.isArray(record['rows']) ? record['rows'] as BillingReportRow[] : [],
      excelRows: Array.isArray(record['excelRows']) ? record['excelRows'] as SalesReportExcelRow[] : [],
      message: record['message'] as string | null | undefined,
    };
  }

  private normalizeBillingHistoryResponse(response: unknown): BillingReportHistoryResponse {
    const source = this.parseBody(response);
    const record = this.toRecord(source);
    return {
      action: record['action'] as BillingReportHistoryResponse['action'],
      shopId: record['shopId'] as BillingReportHistoryResponse['shopId'],
      reports: Array.isArray(record['reports']) ? record['reports'] as BillingReportHistoryItem[] : [],
    };
  }

  private extractBillingSummary(response: BillingReportResponse): BillingReportSummary {
    const summary = response.summary || {};
    const report = response.report || {};
    return {
      totalRows: summary.totalRows ?? report.totalRows,
      totalClients: summary.totalClients ?? report.totalClients,
      totalAssociations: summary.totalAssociations ?? report.totalAssociations,
      totalInstallmentRows: summary.totalInstallmentRows ?? report.totalInstallmentRows,
      totalRecurringRows: summary.totalRecurringRows ?? report.totalRecurringRows,
      totalPrincipalAmount: summary.totalPrincipalAmount ?? report.totalPrincipalAmount,
      totalMonthlyInstallment: summary.totalMonthlyInstallment ?? report.totalMonthlyInstallment,
    };
  }

  private toPreviewRow(sale: SalesReportSale): SalesReportPreviewRow {
    const saleRecord = sale as Record<string, unknown>;
    const orderCode = this.firstString(saleRecord, ['saleOrderCode', 'orderCode', 'order', 'saleShortCode']);
    return {
      identification: this.firstString(saleRecord, ['identification', 'clientNationalId', 'nationalId']),
      client: this.firstStringFromRecords([
        { record: saleRecord, keys: ['fullName', 'clientFullName', 'clientName', 'name'] },
      ]),
      order: orderCode ? orderCode.slice(-8) : '-',
      date: this.firstString(saleRecord, ['saleDate', 'date']),
      association: this.firstString(saleRecord, ['association', 'shopCompanyName', 'companyName']),
      principalAmount: this.toNumber(this.firstValue(saleRecord, ['principalAmount', 'saleEndPrice', 'netAmount'])),
      termMonths: this.toNullableNumber(this.firstValue(saleRecord, ['termMonths', 'term'])),
      firstInstallmentDate: this.firstString(saleRecord, ['firstInstallmentDate']),
      monthlyInstallment: this.toNumber(this.firstValue(saleRecord, ['monthlyInstallment'])),
      observations: this.firstStringFromRecords([
        { record: saleRecord, keys: ['reportObservations', 'observations', 'products', 'productNames', 'productName'] },
      ]),
    };
  }

  private normalizeExcelRow(row: SalesReportExcelRow): ValidatedSalesReportExcelRow {
    const principalAmount = this.toNumber(row.principalAmount);
    const termMonths = this.toNumber(row.termMonths);
    const fallbackMonthly = termMonths > 0 ? principalAmount / termMonths : 0;
    return {
      ...row,
      identification: String(row.identification ?? '').trim(),
      name: this.normalizeWhitespace(row.name),
      document: row.document ?? 0,
      date: String(row.date ?? '').trim(),
      principalAmount,
      discountAmount: this.toNumber(row.discountAmount),
      netAmount: this.toNumber(row.netAmount || principalAmount),
      termMonths,
      firstInstallmentDate: String(row.firstInstallmentDate ?? '').trim(),
      monthlyInstallment: this.toNumber(row.monthlyInstallment || fallbackMonthly),
      observations: this.normalizeWhitespace(row.observations),
    };
  }

  private isExportableRow(row: ValidatedSalesReportExcelRow): boolean {
    return !!row.identification
      && !!row.name
      && !!row.date
      && Number.isFinite(row.principalAmount)
      && row.principalAmount > 0
      && !!row.firstInstallmentDate
      && Number.isFinite(row.termMonths)
      && row.termMonths > 0;
  }

  private parseLocalDate(value: unknown): Date | string {
    const text = String(value ?? '').trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    if (!match) {
      return text;
    }
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  private setTextCell(worksheet: XLSX.WorkSheet, address: string, value: string): void {
    worksheet[address] = {
      t: 's',
      v: value,
      z: '@',
    } as ExcelCell;
  }

  private setDateFormat(worksheet: XLSX.WorkSheet, address: string): void {
    const cell = worksheet[address] as ExcelCell | undefined;
    if (cell) {
      cell.z = 'dd/mm/yyyy';
    }
  }

  private setMoneyFormat(worksheet: XLSX.WorkSheet, address: string): void {
    const cell = worksheet[address] as ExcelCell | undefined;
    if (cell) {
      cell.z = '#,##0.00';
    }
  }

  private nonNegativeNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const numberValue = Number(value);
      if (!Number.isFinite(numberValue)) {
        return { number: true };
      }
      return numberValue < 0 ? { nonNegative: true } : null;
    };
  }

  private dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const start = control.get('startDate')?.value;
      const end = control.get('endDate')?.value;
      if (!start || !end) {
        return null;
      }
      return String(start) > String(end) ? { dateRange: true } : null;
    };
  }

  private amountRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const min = control.get('minAmount')?.value;
      const max = control.get('maxAmount')?.value;
      if (min === null || min === undefined || min === '' || max === null || max === undefined || max === '') {
        return null;
      }
      return Number(min) > Number(max) ? { amountRange: true } : null;
    };
  }

  private billingDatesRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const reportType = control.get('reportType')?.value;
      const startDate = control.get('startDate')?.value;
      const endDate = control.get('endDate')?.value;
      if (reportType === 'activePlans' && (!startDate || !endDate)) {
        return { billingDatesRequired: true };
      }
      return null;
    };
  }

  private toNumber(value: unknown): number {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === 'string') {
      const normalized = value.replace(/,/g, '').trim();
      if (!normalized) {
        return 0;
      }
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return this.toNumber(value);
  }

  private firstValue(record: Record<string, unknown>, keys: string[]): unknown {
    return keys.map((key) => record[key]).find((value) => value !== null && value !== undefined && String(value) !== '');
  }

  private firstString(record: Record<string, unknown>, keys: string[]): string {
    const value = this.firstValue(record, keys);
    return value === null || value === undefined || String(value).trim() === '' ? '-' : String(value).trim();
  }

  private firstStringFromRecords(sources: Array<{ record: Record<string, unknown>; keys: string[] }>): string {
    for (const source of sources) {
      const value = this.firstValue(source.record, source.keys);
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return String(value).trim();
      }
    }

    return '-';
  }

  private normalizeWhitespace(value: unknown): string {
    return String(value ?? '').replace(/\s+/g, ' ').trim();
  }

  private parseBody(response: unknown): unknown {
    const record = this.toRecord(response);
    if (typeof record['body'] === 'string') {
      try {
        return JSON.parse(record['body']);
      } catch {
        return response;
      }
    }
    return response;
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? value as Record<string, unknown> : {};
  }

  private toNullableRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' ? value as Record<string, unknown> : null;
  }

  private toDisplayDate(value: unknown): string {
    if (!value) {
      return '';
    }
    const text = String(value);
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    if (isoMatch) {
      return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
    }
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) {
      return text;
    }
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }

  private getBillingDateRange(): { startDate: string; endDate: string } {
    return {
      startDate: String(this.filtersForm.get('startDate')?.value || '').trim(),
      endDate: String(this.filtersForm.get('endDate')?.value || '').trim(),
    };
  }

  private getBillingShopCompanyId(): number {
    const value = Number(this.filtersForm.get('shopCompanyId')?.value);
    return Number.isFinite(value) ? value : 0;
  }

  private async getBillingCreatedBy(): Promise<string> {
    const currentEmail = this.authService.getCurrentUserEmail()?.trim();
    if (currentEmail) {
      return currentEmail;
    }

    try {
      const user = await this.authService.getCurrentUserDetails();
      return String(user?.email || user?.attributes?.['email'] || user?.username || '').trim();
    } catch {
      return '';
    }
  }

  private toIsoDatePart(value: unknown): string {
    const text = String(value || '').trim();
    const match = /^\d{4}-\d{2}-\d{2}/.exec(text);
    return match ? match[0] : this.safeFilePart(text);
  }

  private safeFilePart(value: unknown): string {
    return String(value || '').replace(/[^0-9A-Za-z_-]/g, '');
  }

  private getTodayIsoDate(): string {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }
}
